import { useContext, useCallback, useEffect, useRef, useState, useMemo } from "react"
import classNames from "classnames"
import { toUpper, pick } from "lodash"
import { Box, Button, IconButton, Typography } from "@mui/material"
import { Close, TuneOutlined } from "@mui/icons-material"

import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import useDebouncedCallback from "hooks/debounced-callback"
import { useOptimization } from "../../hooks/optimizer"
import { usePortfolioHoldings } from "hooks/rebalancer"
import { useAccountAlias, useIsDashboardOpLayoutEnabled } from "resources/account"
import { useForecasts } from "resources/forecasts"

import { AnimatedLoadingButton, Benchmark, Setup, Rules, Error, FactorRulesWrapper, UniverseSelector } from "./components"
import { getDefaultConfiguration, type FormSections } from "./configuration-sections"
import { getPortfolioType, transformConfigurationToForm, transformFormToConfiguration, calculateCurrentMarketValues, calculatePortfolioNetLong } from "./components/helpers/configuration"
import { useOptimizeForm, OptimizeFormContext } from "./form-context"
import { useTotalRisk } from "./hooks/use-total-risk"
import { OptimizeExperimentsProps, BaseInstrumentWithName } from "./types"
import type { PositionSetInput } from "./components/factor-rules/types"

import "./styles.scss"

export default function OptimizeExperiments({ context, modelMetadata }: OptimizeExperimentsProps) {
  const isDashboardOpLayoutEnabled = useIsDashboardOpLayoutEnabled()
  const {
    setIsOptimizedExperimentsDrawerOpen,
    isOptimizedExperimentsDrawerOpen,
    endDate,
    modelId,
    base: dashboardBase,
    isBaseOverridden,
    baseOverride,
    instrumentsMetadata,
    setIsBaseOverridden,
    setBaseOverride,
    setComparisonInstrument,
    setOptimizeConfiguration,
    setPreviewOptimizeConfiguration,
    previewOptimizeConfiguration,
    optimizeConfiguration,
    instrument,
  } = useContext(context)
  const account = useAccountAlias()

  // Derive the actual base to use: baseOverride when override is ON, dashboard base otherwise
  const optimizerBase = useMemo(() => {
    if (isBaseOverridden) {
      return baseOverride
    }

    if (dashboardBase) {
      const name = instrumentsMetadata?.base?.name || dashboardBase.id
      return { ...dashboardBase, name }
    }

    return null
  }, [isBaseOverridden, baseOverride, dashboardBase, instrumentsMetadata])

  // Strip name property when passing to queries (only keep id, type, weight and experimentId)
  const optimizerBaseForQueries = useMemo(() => {
    if (!optimizerBase) return null
    return pick(optimizerBase, ["id", "type", "weight", "experimentId"]) as SelectedTypedInstrument
  }, [optimizerBase])

  const [totalRisk, totalRiskStatus] = useTotalRisk(instrument, modelId, endDate, optimizerBaseForQueries)
  const totalRiskValue = totalRisk?.values.totalRisk?.value
  const [forecasts, forecastsStatus] = useForecasts()
  const pendingForecastRef = useRef<boolean>(false)
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const portfolioMarketValues = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return { longMarketValue: 0, shortMarketValue: 0 }
    }
    return calculateCurrentMarketValues(portfolioHoldings)
  }, [portfolioHoldings, portfolioHoldingsStatus])
  const portfolioNetLong = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return 0
    }
    return calculatePortfolioNetLong(portfolioMarketValues)
  }, [portfolioMarketValues, portfolioHoldingsStatus])

  const portfolioType = useMemo(() => {
    return getPortfolioType(portfolioMarketValues)
  }, [portfolioMarketValues])

  const getInitialConfiguration = () => {
    const configuration = previewOptimizeConfiguration || optimizeConfiguration

    if (configuration) {
      try {
        return transformConfigurationToForm(configuration)
      } catch {
        return getDefaultConfiguration()
      }
    }

    return getDefaultConfiguration()
  }

  const [formError, setFormError] = useState<string | null>(null)
  const initialEndDate = useRef(endDate)

  const form = useOptimizeForm({
    defaultValues: getInitialConfiguration(),
    onSubmit: async ({ value }: { value: FormSections }) => {
      if (canOptimize) {
        try {
          const currentConfiguration = transformFormToConfiguration(
            endDate,
            value.setup,
            value.rules,
            portfolioMarketValues,
            value.universe,
          )
          setFormError(null)
          isSaveRef.current = true
          handleRunOptimization(true)
          setOptimizeConfiguration(currentConfiguration)
          setPreviewOptimizeConfiguration(null)
        } catch {
          isSaveRef.current = false
          setFormError("Invalid form input. Please check your inputs and try again.")
        }
      }
    },
  })

  const [formValues, setFormValues] = useState(form.state.values)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setFormValues(form.state.values)
    })
    return unsubscribe
  }, [form])

  const prevInstrumentIdRef = useRef<string | undefined>(undefined)
  const prevModelIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    // Reset factor rules when instrument or modelId changes
    // Don't reset on initial mount - only when values actually change
    const hasInstrumentChanged =
      prevInstrumentIdRef.current !== undefined
      && prevInstrumentIdRef.current !== instrument?.id
      && instrument?.id !== undefined

    const hasModelChanged =
      prevModelIdRef.current !== undefined
      && prevModelIdRef.current !== modelId
      && modelId !== undefined

    if ((hasInstrumentChanged || hasModelChanged) && instrument?.id && modelId) {
      form.setFieldValue("rules.exposure", [])
      form.setFieldValue("rules.composition", [])
    }

    if (instrument?.id !== undefined) {
      prevInstrumentIdRef.current = instrument.id
    }
    if (modelId !== undefined) {
      prevModelIdRef.current = modelId
    }
  }, [modelId, form, instrument?.id])

  const {
    runOptimization,
    optimizedPositionSet,
    optimizationStatus,
    optimizationError,
    getForecastData,
    forecastData,
    forecastDataStatus,
  } = useOptimization(
    instrument,
    modelId,
    endDate,
    optimizerBaseForQueries,
    formValues,
    portfolioMarketValues,
  )

  const hasForecastDataError = Boolean(forecastDataStatus === ERROR && formValues.setup.forecast && formValues.setup.forecast !== "none")
  const isOptimizing = optimizationStatus === LOADING
  const hasError = hasForecastDataError || optimizationStatus === ERROR || !!optimizationError
  const canOptimize = Boolean(
    modelMetadata?.id
    && instrument?.id
    && !isOptimizing
    && modelId
    && endDate,
  )

  const isSaveRef = useRef<boolean>(false)

  useEffect(() => {
    if (hasError) {
      isSaveRef.current = false
    } else if (optimizedPositionSet) {
      setComparisonInstrument(optimizedPositionSet)
      if (isSaveRef.current) {
        setIsOptimizedExperimentsDrawerOpen(false)
        isSaveRef.current = false
      }
    }
  }, [optimizedPositionSet, setComparisonInstrument, setIsOptimizedExperimentsDrawerOpen, hasError])

  const handleRunOptimization = useCallback((forceRun?: boolean) => {
    const selectedForecast = formValues.setup.forecast
    if (selectedForecast && selectedForecast !== "none") {
      getForecastData()
      pendingForecastRef.current = true
      return
    }
    pendingForecastRef.current = false
    runOptimization(forceRun)
  }, [formValues.setup.forecast, getForecastData, runOptimization])

  const forecastDataError = hasForecastDataError ? "Error fetching forecast data. Select a different forecast." : null

  useEffect(() => {
    if (pendingForecastRef.current && forecastData && forecastDataStatus === OK) {
      pendingForecastRef.current = false
      runOptimization(true)
    } else if (hasForecastDataError) {
      pendingForecastRef.current = false
    }
  }, [forecastData, forecastDataStatus, runOptimization, hasForecastDataError])

  // Only run and save preview configuration when the panel is open (live preview/edit mode)
  // If there's a change, wait 3 seconds to see if there's another change
  // If there's no change, run the optimization
  const debouncedRunOptimization = useDebouncedCallback(() => {
    if (canOptimize && isOptimizedExperimentsDrawerOpen && form.state.canSubmit) {
      try {
        const values = form.state.values
        const currentConfiguration = transformFormToConfiguration(
          endDate,
          values.setup,
          values.rules,
          portfolioMarketValues,
          values.universe,
        )
        setFormError(null)
        handleRunOptimization()
        setPreviewOptimizeConfiguration(currentConfiguration)
      } catch {
        setFormError("Invalid form input. Please check your inputs and try again.")
      }
    }
  }, 3000)

  const [isDirty, setIsDirty] = useState(form.state.isDirty)

  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      setIsDirty(form.state.isDirty)
    })
    return unsubscribe
  }, [form])

  useEffect(() => {
    if (isDirty) {
      debouncedRunOptimization()
    }
  }, [formValues, isDirty, debouncedRunOptimization])

  useEffect(() => {
    const hasDateChanged = initialEndDate.current !== endDate
    if (hasDateChanged && isOptimizedExperimentsDrawerOpen) {
      debouncedRunOptimization()
    }
  }, [endDate, isOptimizedExperimentsDrawerOpen, debouncedRunOptimization])

  const handleSaveAndOptimize = useCallback(() => {
    form.handleSubmit()
  }, [form])

  const handleClosePanel = useCallback(() => {
    setIsOptimizedExperimentsDrawerOpen(false)
    setFormError(null)
  }, [setIsOptimizedExperimentsDrawerOpen, setFormError])

  const handleBaseChange = useCallback((selectedInstrument: BaseInstrumentWithName | null) => {
    if (!selectedInstrument) {
      setBaseOverride(null)
      return
    }

    const { id, name, type } = selectedInstrument
    // Type guard because experimentId only exists on SelectedPortfolio/SelectedExperiment
    const experimentId = "experimentId" in selectedInstrument ? selectedInstrument.experimentId : undefined
    const baseType = toUpper(type) as SelectedTypedInstrument["type"]
    const baseToSet: BaseInstrumentWithName = {
      type: baseType,
      id,
      name: name || id,
      weight: 1,
      ...(experimentId && { experimentId }),
    } as BaseInstrumentWithName

    setBaseOverride(baseToSet)
  }, [setBaseOverride])

  const handleBaseOverrideToggle = useCallback((checked: boolean) => {
    setIsBaseOverridden(checked)
    if (checked) {
      // When enabling override, initialize with dashboard base
      if (dashboardBase) {
        const name = instrumentsMetadata?.base?.name || dashboardBase.id
        setBaseOverride({ ...dashboardBase, name } as BaseInstrumentWithName)
      } else {
        setBaseOverride(null)
      }
    }
  }, [dashboardBase, instrumentsMetadata, setBaseOverride, setIsBaseOverridden])

  return (
    <div className={classNames("optimize-experiments-content", { "use-dashboard-layout": isDashboardOpLayoutEnabled })}>
      <div className="content">
        <div className="header">
          <TuneOutlined color="primary" />
          <Typography variant="h6">
            <span>Optimized Experiments</span>
          </Typography>
          <div className="header-actions">
            <IconButton onClick={handleClosePanel} size="medium" color="inherit">
              <Close />
            </IconButton>
          </div>
        </div>
        <OptimizeFormContext.Provider value={form}>
          <Box className="body">
            <Box className="body-section">
              <Benchmark
                account={account}
                modelId={modelId}
                baseInstrument={optimizerBase}
                handleInstrumentSelected={handleBaseChange}
                isBaseOverridden={isBaseOverridden}
                onBaseOverrideToggle={handleBaseOverrideToggle}
              />

              <Setup
                currentTotalRiskValue={totalRiskValue}
                totalRiskStatus={totalRiskStatus}
                forecasts={forecasts || []}
                forecastsStatus={forecastsStatus}
                hasBase={Boolean(optimizerBase)}
              />

            </Box>
            <Box className="body-section">
              <UniverseSelector modelId={modelId} />
            </Box>
            <Box className="body-section">
              <Rules portfolioType={portfolioType} portfolioNetLong={portfolioNetLong} />
            </Box>
            <Box className="body-section">
              <FactorRulesWrapper positionSet={instrument as PositionSetInput} portfolioMarketValues={portfolioMarketValues} />
            </Box>
          </Box>
          <Box className="actions">
            {isOptimizing ? (
              <AnimatedLoadingButton text="Optimizing..." />
            ) : (
              <Button
                variant="contained"
                color={hasError ? "error" : "primary"}
                onClick={handleSaveAndOptimize}
                disabled={!canOptimize || !form.state.canSubmit || hasForecastDataError}
              >
                {hasError ? "Optimization Failed - Retry" : "Run and Save"}
              </Button>
            )}
          </Box>
          <Error error={formError || forecastDataError || optimizationError} />
        </OptimizeFormContext.Provider>
      </div>
    </div>
  )
}
